import { serverBaseUrl } from "../config/envs";

const object = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
export const CP_SIGN_CONSENT_VERSION = "adopt-and-sign-v1";
export const CP_SIGN_CONSENT_TEXT = "By selecting Adopt and Sign, I agree that the signature and initials will be the electronic representation of my signature and initials for all purposes when I (or my agent) use them on documents, including legally binding contracts.";
const calendarDate = (value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
  && !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;

function validSigning(data) {
  if (data.signing === undefined) return data.signing_enabled === false;
  const signing = data.signing;
  const keys = new Set();
  const roleFields = data.document.snapshot.fields.filter((field) => field.signer_role === data.document.role_key);
  return object(signing) && ["eligible", "waiting", "signed", "blocked"].includes(signing.state)
    && data.signing_enabled === (signing.state === "eligible")
    && (signing.state === "waiting" ? ["physician", "operator"].includes(signing.waiting_on) : signing.waiting_on === null)
    && signing.consent?.version === CP_SIGN_CONSENT_VERSION && signing.consent.text === CP_SIGN_CONSENT_TEXT
    && calendarDate(data.context.signing_date)
    && Array.isArray(signing.fields) && signing.fields.length === roleFields.length && signing.fields.every((field) => {
      if (!object(field) || typeof field.field_key !== "string" || keys.has(field.field_key)) return false;
      keys.add(field.field_key);
      const saved = roleFields.find((entry) => entry.field_key === field.field_key);
      return saved && field.field_type === saved.field_type && field.required === saved.required
        && typeof field.label === "string" && typeof field.required === "boolean" && typeof field.editable === "boolean"
        && ["text", "date", "checkbox", "signature"].includes(field.field_type)
        && ["prefilled", "ask_signer", "leave_blank", "signature", "hold_document", "unavailable", "invalid"].includes(field.status)
        && (field.value === null || typeof field.value === (field.field_type === "checkbox" ? "boolean" : "string"))
        && (field.field_type !== "signature" || (!field.editable && field.status === "signature"))
        && (signing.state !== "eligible" || !["hold_document", "unavailable", "invalid"].includes(field.status));
    });
}

export async function getCpDocumentSignContext(secret) {
  let response;
  try {
    response = await fetch(`${serverBaseUrl}/cp_document_sign_context`, {
      method: "GET", headers: { "sign-secret-id": secret }, credentials: "omit", cache: "no-store", referrerPolicy: "no-referrer",
    });
  } catch {
    throw new Error("Could not reach this document. Check your connection and try again.");
  }
  let body;
  try { body = await response.json(); } catch { /* A proxy may return HTML, not the API envelope. */ }
  const data = body?.data;
  const document = data?.document;
  const profile = data?.context?.signers?.[document?.role_key];
  const valid = typeof data?.signing_enabled === "boolean" && ["physician", "operator"].includes(document?.role_key)
    && Number.isInteger(document?.state_id) && document.state_id >= 1 && document.state_id <= 52
    && data.context?.state_id === document.state_id && typeof data.context?.signers === "object"
    && data.context.signers !== null && !Array.isArray(data.context.signers)
    && (data.context.signing_date === null || typeof data.context.signing_date === "string")
    && (profile === null || (object(profile) && (profile.licenses === null || (Array.isArray(profile.licenses) && profile.licenses.every(object)))))
    && Array.isArray(document.snapshot?.fields) && document.snapshot.fields.every(object) && typeof document.snapshot?.pdf?.url === "string"
    && document.snapshot.pdf.url.length > 0 && Array.isArray(document.snapshot.pdf.pages)
    && document.snapshot.pdf.pages.every(object)
    && Array.isArray(data.record_sources) && data.record_sources.every((source) => object(source) && Array.isArray(source.roles))
    && validSigning(data);
  if (response.ok && body?.success === true && valid) return data;

  const message = response.status === 404 && body?.messageCode === 1156 ? "This document is not available right now. Please try again later."
    : response.status === 409 ? "This document is no longer available for signing. Contact your administrator if you need a copy."
      : [400, 401, 403, 404].includes(response.status) ? "This document link is unavailable. Ask your administrator for a valid link."
        : response.ok ? "The document returned an unexpected response. Please reload; if this continues, contact your administrator."
          : "This document is temporarily unavailable. Please try again.";
  const failure = new Error(message);
  failure.status = response.status;
  failure.code = body?.messageCode;
  throw failure;
}
